import { NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { NodeView } from 'prosemirror-view';
import { Info, AlertTriangle, Lightbulb, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CalloutType } from './Callout';

interface CalloutNodeViewProps {
  node: ProseMirrorNode;
  updateAttributes: (attributes: Record<string, any>) => void;
  deleteNode: () => void;
  selected: boolean;
  editor: any;
}

const calloutConfig = {
  info: {
    icon: Info,
    label: 'Info',
    className: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950',
    iconClassName: 'text-blue-600 dark:text-blue-400',
  },
  warning: {
    icon: AlertTriangle,
    label: 'Warning',
    className: 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950',
    iconClassName: 'text-yellow-600 dark:text-yellow-400',
  },
  tip: {
    icon: Lightbulb,
    label: 'Tip',
    className: 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950',
    iconClassName: 'text-green-600 dark:text-green-400',
  },
  danger: {
    icon: AlertCircle,
    label: 'Danger',
    className: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950',
    iconClassName: 'text-red-600 dark:text-red-400',
  },
};

export default function CalloutNodeView({
  node,
  updateAttributes,
  deleteNode,
  selected,
}: CalloutNodeViewProps) {
  try {
    const type = (node.attrs.type as CalloutType) || 'info';
    const config = calloutConfig[type];
    const Icon = config.icon;

  const handleTypeChange = (newType: CalloutType) => {
    updateAttributes({ type: newType });
  };

  return (
    <NodeViewWrapper
      className={`callout relative rounded-lg border p-4 my-4 ${config.className} ${
        selected ? 'ring-2 ring-primary ring-offset-2' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 mt-1 ${config.iconClassName}`}>
          <Icon className="h-5 w-5" />
        </div>

        <div className="flex-1 min-w-0">
          <NodeViewContent className="callout-content prose prose-sm max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0" />
        </div>

        <div className="flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 opacity-50 hover:opacity-100"
              >
                <Icon className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {Object.entries(calloutConfig).map(([key, config]) => {
                const ConfigIcon = config.icon;
                return (
                  <DropdownMenuItem
                    key={key}
                    onClick={() => handleTypeChange(key as CalloutType)}
                    className="flex items-center gap-2"
                  >
                    <ConfigIcon className={`h-4 w-4 ${config.iconClassName}`} />
                    {config.label}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuItem
                onClick={deleteNode}
                className="flex items-center gap-2 text-destructive"
              >
                Remove callout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </NodeViewWrapper>
  );
  } catch (error) {
    console.error('Error rendering CalloutNodeView:', error);
    return (
      <NodeViewWrapper className="callout relative rounded-lg border p-4 my-4 border-red-200 bg-red-50">
        <div className="text-red-600">
          <p>Error rendering callout</p>
          <NodeViewContent />
        </div>
      </NodeViewWrapper>
    );
  }
}